import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Grid, MenuItem, Stack, TextField } from "@mui/material";
import { useRecoilValue } from "recoil";
import { authState } from "../../Recoil/RecoilState";
import { fetchLostFoundCameras } from "../../services/lostFoundApi";
import { EVENT_STATUS_OPTIONS, OBJECT_CLASSES, titleCase } from "./lostFoundConstants";

const EMPTY = {
  camera_id: "",
  status_filter: "",
  target_class: "",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
};

/**
 * Filter popup for Lost & Found events, plugged into IncidentVideosTab via its
 * FilterComponent prop: same { filter, changeFilterHandle } props and ref.submit().
 * Keys are the GET lost-found/events query parameters.
 */
const LostFoundEventFilter = forwardRef(({ filter, changeFilterHandle }, ref) => {
  const { token } = useRecoilValue(authState);
  const [values, setValues] = useState({ ...EMPTY, ...filter });
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchLostFoundCameras(token, false)
      .then((data) => !cancelled && setCameras(Array.isArray(data) ? data : []))
      .catch(() => !cancelled && setCameras([]));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const set = (field) => (event) => setValues((current) => ({ ...current, [field]: event.target.value }));

  useImperativeHandle(ref, () => ({
    submit: () => {
      // Only send what is set: IncidentVideosTab forwards every truthy key.
      changeFilterHandle(Object.fromEntries(Object.entries(values).filter(([, v]) => v)));
    },
  }));

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth size="small" label="Camera" value={values.camera_id} onChange={set("camera_id")}>
            <MenuItem value="">All cameras</MenuItem>
            {cameras.map((cam) => (
              <MenuItem key={cam.stream_id} value={cam.stream_id}>
                {cam.camera_name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth size="small" label="Status" value={values.status_filter} onChange={set("status_filter")}>
            <MenuItem value="">All statuses</MenuItem>
            {EVENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth size="small" label="Object type" value={values.target_class} onChange={set("target_class")}>
            <MenuItem value="">Any type</MenuItem>
            {OBJECT_CLASSES.map((value) => (
              <MenuItem key={value} value={value}>
                {titleCase(value)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} />
        <Grid item xs={6}>
          <TextField fullWidth size="small" type="date" label="From date" value={values.start_date}
            onChange={set("start_date")} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" type="time" label="From time" value={values.start_time}
            onChange={set("start_time")} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" type="date" label="To date" value={values.end_date}
            onChange={set("end_date")} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={6}>
          <TextField fullWidth size="small" type="time" label="To time" value={values.end_time}
            onChange={set("end_time")} InputLabelProps={{ shrink: true }} />
        </Grid>
      </Grid>
      <Button onClick={() => setValues(EMPTY)} sx={{ alignSelf: "flex-start" }}>
        Clear all filters
      </Button>
    </Stack>
  );
});

export default LostFoundEventFilter;
